import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  X,
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Info,
  GripVertical,
  SortAsc,
} from 'lucide-react';
import {
  ModalOverlay,
  ModalPanel,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalActions,
  ModalButton,
  ModalStatusMessage,
} from './shared/Modal';
import { useUploads } from '../context/UploadsContext';

const DropZone = styled.div<{ $disabled: boolean; $dragging: boolean; $compact: boolean }>`
  position: relative;
  border: 2px dashed ${({ $disabled }) => ($disabled ? '#d1d5db' : '#3b82f6')};
  border-radius: 10px;
  padding: ${({ $compact }) => ($compact ? '16px' : '40px 20px')};
  text-align: center;
  background: ${({ $dragging }) => ($dragging ? '#eff6ff' : '#fafafa')};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s ease, border-color 0.2s ease;
`;

const HiddenInput = styled.input`
  display: none;
`;

const StagedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
`;

const StagedRow = styled.div<{ $failed?: boolean; $dragging?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ $failed }) => ($failed ? '#fca5a5' : '#e5e7eb')};
  background: ${({ $failed, $dragging }) => ($dragging ? '#f3f4f6' : $failed ? '#fef2f2' : '#ffffff')};
  opacity: ${({ $dragging }) => ($dragging ? 0.6 : 1)};
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  color: #9ca3af;
  cursor: grab;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
`;

const RowBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const RowTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowMeta = styled.div`
  font-size: 11px;
  color: #6b7280;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 5px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: #111827;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const TrayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 14px;
  font-size: 12px;
  color: #6b7280;
`;

const TraySortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 5px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: #f9fafb;
  }
`;

const CaseNameLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 14px;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
`;

const CaseNameInput = styled.input`
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 13px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const StatusGlyph = styled.span<{ $status: StagedStatus }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: ${({ $status }) =>
    $status === 'done' ? '#059669' : $status === 'failed' ? '#dc2626' : '#3b82f6'};
`;

type StagedStatus = 'pending' | 'uploading' | 'done' | 'failed';

interface StagedFile {
  id: string;
  file: File;
  uploadId: string;
  fileFingerprint: string;
  attemptNum: number;
}

interface UploadModalProps {
  visible: boolean;
  onClose: () => void;
  onCaseCreated: () => void;
  uploadDisabled?: boolean;
}

type Feedback = { tone: 'info' | 'success' | 'error'; message: string } | null;

const makeId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// FastAPI errors: `detail` is usually a string, but validation errors yield an
// array of `{type, loc, msg, input}` objects. Render both as a readable string.
const extractErrorMessage = (err: any): string | undefined => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map(item => (typeof item?.msg === 'string' ? item.msg : JSON.stringify(item)))
      .join('; ');
  }
  if (typeof err?.message === 'string') return err.message;
  return undefined;
};

// Natural-order comparator for filenames like "part1.pdf", "part2.pdf", ..., "part10.pdf".
const naturalCompare = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

export const UploadModal: React.FC<UploadModalProps> = ({
  visible,
  onClose,
  onCaseCreated,
  uploadDisabled = false,
}) => {
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [caseName, setCaseName] = useState<string>('');
  const [caseNameTouched, setCaseNameTouched] = useState<boolean>(false);
  const [dragging, setDragging] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [submitting, setSubmitting] = useState(false);
  const dragRowIndex = useRef<number | null>(null);
  const [draggedRowId, setDraggedRowId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const totalBytes = useMemo(() => staged.reduce((sum, s) => sum + s.file.size, 0), [staged]);
  const canSubmit = staged.length > 0 && caseName.trim().length > 0 && !submitting;
  const { startCaseUpload } = useUploads();

  // Auto-fill case name from the first file's basename until the user edits it.
  useEffect(() => {
    if (caseNameTouched) return;
    if (staged.length === 0) {
      setCaseName('');
      return;
    }
    const first = staged[0].file.name.replace(/\.pdf$/i, '');
    setCaseName(first);
  }, [staged, caseNameTouched]);

  // Reset state when modal closes. Uploads in flight are owned by
  // UploadsProvider and survive unmount; we only clear modal-local state.
  useEffect(() => {
    if (!visible) {
      setStaged([]);
      setCaseName('');
      setCaseNameTouched(false);
      setDragging(false);
      setFeedback(null);
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files).filter(f => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
      const rejected = Array.from(files).length - list.length;
      if (rejected > 0) {
        setFeedback({ tone: 'error', message: `${rejected} non-PDF file${rejected === 1 ? '' : 's'} skipped.` });
      } else if (list.length > 0) {
        setFeedback(null);
      }
      const newEntries: StagedFile[] = list.map(file => ({
        id: makeId(),
        file,
        uploadId: `up-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
        fileFingerprint: `${file.name}|${file.size}|${file.lastModified}`,
        attemptNum: 1,
      }));
      setStaged(prev => [...prev, ...newEntries]);
    },
    [],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setDragging(false);
      if (uploadDisabled || event.dataTransfer.files.length === 0) return;
      addFiles(event.dataTransfer.files);
    },
    [addFiles, uploadDisabled],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (!uploadDisabled) {
        setDragging(true);
      }
    },
    [uploadDisabled],
  );

  const handleDragLeave = useCallback(() => setDragging(false), []);

  const handleRemove = useCallback((id: string) => {
    setStaged(prev => prev.filter(s => s.id !== id));
  }, []);

  const handleSortByFilename = useCallback(() => {
    setStaged(prev => [...prev].sort((a, b) => naturalCompare(a.file.name, b.file.name)));
  }, []);

  const handleRowDragStart = useCallback((index: number, id: string) => {
    dragRowIndex.current = index;
    setDraggedRowId(id);
  }, []);

  const handleRowDragOver = useCallback((event: React.DragEvent, hoverIndex: number) => {
    event.preventDefault();
    const from = dragRowIndex.current;
    if (from == null || from === hoverIndex) return;
    setStaged(prev => {
      const copy = prev.slice();
      const [moved] = copy.splice(from, 1);
      copy.splice(hoverIndex, 0, moved);
      return copy;
    });
    dragRowIndex.current = hoverIndex;
  }, []);

  const handleRowDragEnd = useCallback(() => {
    dragRowIndex.current = null;
    setDraggedRowId(null);
  }, []);

  const handleCreateCase = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      await startCaseUpload({
        caseName: caseName.trim() || undefined,
        files: staged.map(s => ({
          file: s.file,
          id: s.id,
          uploadId: s.uploadId,
          fileFingerprint: s.fileFingerprint,
          attemptNum: s.attemptNum,
        })),
      });
      onCaseCreated();
      // The parent closes the modal; uploads continue in the background
      // owned by UploadsProvider. Progress is visible in the jobs UI.
    } catch (error: any) {
      const message = extractErrorMessage(error) || 'Failed to create case.';
      setFeedback({ tone: 'error', message });
      setSubmitting(false);
    }
  }, [canSubmit, staged, caseName, startCaseUpload, onCaseCreated]);

  if (!visible) {
    return null;
  }

  return (
    <ModalOverlay $visible={visible}>
      <ModalPanel $width="640px">
        <ModalHeader>
          <ModalTitle>Create case</ModalTitle>
          <ModalCloseButton type="button" onClick={onClose}>
            <X size={20} />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          <DropZone
            $disabled={uploadDisabled}
            $dragging={dragging}
            $compact={staged.length > 0}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => {
              if (uploadDisabled) return;
              fileInputRef.current?.click();
            }}
          >
            <Upload size={staged.length > 0 ? 24 : 48} style={{ margin: '0 auto 8px', color: '#6b7280' }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
              {staged.length === 0 ? 'Drop PDFs here or click to browse' : 'Add more PDFs'}
            </div>
            {staged.length === 0 && (
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
                Multiple PDFs become one case, joined in list order.
              </div>
            )}
          </DropZone>

          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            multiple
            onChange={event => {
              if (event.target.files && event.target.files.length > 0) {
                addFiles(event.target.files);
              }
              // Reset the input so the same file can be picked again if removed.
              event.target.value = '';
            }}
            disabled={uploadDisabled}
          />

          {staged.length > 0 && (
            <>
              <TrayHeader>
                <span>
                  {staged.length} file{staged.length === 1 ? '' : 's'} · {formatBytes(totalBytes)}
                </span>
                <TraySortButton type="button" onClick={handleSortByFilename} disabled={staged.length < 2}>
                  <SortAsc size={12} />
                  Sort by filename
                </TraySortButton>
              </TrayHeader>
              <StagedList>
                {staged.map((entry, index) => (
                  <StagedRow
                    key={entry.id}
                    $failed={false}
                    $dragging={draggedRowId === entry.id}
                    draggable
                    onDragStart={() => handleRowDragStart(index, entry.id)}
                    onDragOver={e => handleRowDragOver(e, index)}
                    onDragEnd={handleRowDragEnd}
                  >
                    <DragHandle>
                      <GripVertical size={14} />
                    </DragHandle>
                    <StatusGlyph $status="pending">
                      <FileText size={16} />
                    </StatusGlyph>
                    <RowBody>
                      <RowTitle title={entry.file.name}>{entry.file.name}</RowTitle>
                      <RowMeta>
                        <span>{formatBytes(entry.file.size)}</span>
                      </RowMeta>
                    </RowBody>
                    <RowActions>
                      <IconButton
                        type="button"
                        title="Remove"
                        onClick={() => handleRemove(entry.id)}
                        disabled={submitting}
                      >
                        <X size={14} />
                      </IconButton>
                    </RowActions>
                  </StagedRow>
                ))}
              </StagedList>

              <CaseNameLabel>
                Case name
                <CaseNameInput
                  type="text"
                  value={caseName}
                  onChange={e => {
                    setCaseName(e.target.value);
                    setCaseNameTouched(true);
                  }}
                  placeholder="Case name"
                  disabled={submitting}
                />
              </CaseNameLabel>
            </>
          )}

          {feedback && (
            <ModalStatusMessage $tone={feedback.tone} style={{ marginTop: 12 }}>
              {feedback.tone === 'error' ? (
                <AlertCircle size={16} />
              ) : feedback.tone === 'success' ? (
                <CheckCircle size={16} />
              ) : (
                <Info size={16} />
              )}
              <span>{feedback.message}</span>
            </ModalStatusMessage>
          )}
        </ModalBody>

        <ModalActions>
          <ModalButton $variant="secondary" type="button" onClick={onClose}>
            Cancel
          </ModalButton>
          <ModalButton
            $variant="primary"
            type="button"
            onClick={handleCreateCase}
            disabled={!canSubmit || uploadDisabled}
          >
            <Upload size={16} />
            {submitting ? 'Creating…' : 'Create case'}
          </ModalButton>
        </ModalActions>
      </ModalPanel>
    </ModalOverlay>
  );
};

export default UploadModal;
